import {
  CollectionCreateSchema,
  CollectionSchema,
  CollectionUpdateSchema,
  createUuid,
  normalizeLabel,
  PersonCreateSchema,
  PersonSchema,
  PersonUpdateSchema,
  TagCreateSchema,
  TagSchema,
  TagUpdateSchema,
  type Collection,
  type CreateCollectionInput,
  type CreatePersonInput,
  type CreateTagInput,
  type Person,
  type Tag,
  type UpdateCollectionInput,
  type UpdatePersonInput,
  type UpdateTagInput,
} from "../../domain";
import type { DuaWorkspaceDatabase } from "../database";
import {
  bySortOrderThenName,
  DuplicateEntityError,
  EntityNotFoundError,
  parseEntityId,
  type RepositoryOptions,
} from "./repository-utils";

export interface OrganisationalListOptions {
  includeArchived?: boolean;
  includeDeleted?: boolean;
}

export class CollectionRepository {
  readonly #database: DuaWorkspaceDatabase;
  readonly #now: () => string;
  readonly #createId: () => string;

  constructor(database: DuaWorkspaceDatabase, options: RepositoryOptions = {}) {
    this.#database = database;
    this.#now = options.now ?? (() => new Date().toISOString());
    this.#createId = options.createId ?? createUuid;
  }

  async list(options: OrganisationalListOptions = {}): Promise<Collection[]> {
    let records = await this.#database.collections.toArray();
    if (options.includeDeleted !== true) {
      records = records.filter((record) => record.deletedAt === undefined);
    }
    if (options.includeArchived !== true) {
      records = records.filter((record) => record.archivedAt === undefined);
    }
    return records
      .map((record) => CollectionSchema.parse(record))
      .sort(bySortOrderThenName((record) => record.name));
  }

  async getById(id: string, includeDeleted = false): Promise<Collection | undefined> {
    const record = await this.#database.collections.get(parseEntityId(id));
    if (record === undefined || (!includeDeleted && record.deletedAt !== undefined)) {
      return undefined;
    }
    return CollectionSchema.parse(record);
  }

  async #assertUniqueName(normalizedName: string, exceptId?: string): Promise<void> {
    const candidates = await this.#database.collections.where("normalizedName").equals(normalizedName).toArray();
    if (candidates.some((record) => record.deletedAt === undefined && record.id !== exceptId)) {
      throw new DuplicateEntityError("Collection", normalizedName);
    }
  }

  async create(input: CreateCollectionInput): Promise<Collection> {
    const values = CollectionCreateSchema.parse(input);
    const normalizedName = normalizeLabel(values.name);
    const timestamp = this.#now();
    const record = CollectionSchema.parse({
      id: this.#createId(),
      ...values,
      normalizedName,
      createdAt: timestamp,
      updatedAt: timestamp,
    });
    return this.#database.transaction("rw", this.#database.collections, async () => {
      await this.#assertUniqueName(normalizedName);
      await this.#database.collections.add(record);
      return record;
    });
  }

  async update(id: string, input: UpdateCollectionInput): Promise<Collection> {
    const entityId = parseEntityId(id);
    const patch = CollectionUpdateSchema.parse(input);
    return this.#database.transaction("rw", this.#database.collections, async () => {
      const current = await this.#database.collections.get(entityId);
      if (current === undefined || current.deletedAt !== undefined) {
        throw new EntityNotFoundError("Collection", entityId);
      }
      const normalizedName = patch.name === undefined ? current.normalizedName : normalizeLabel(patch.name);
      await this.#assertUniqueName(normalizedName, entityId);
      const updated = CollectionSchema.parse({
        ...current,
        ...patch,
        normalizedName,
        updatedAt: this.#now(),
      });
      await this.#database.collections.put(updated);
      return updated;
    });
  }

  async archive(id: string): Promise<Collection> {
    return this.update(id, { archivedAt: this.#now() });
  }

  async restoreArchive(id: string): Promise<Collection> {
    const entityId = parseEntityId(id);
    return this.#database.transaction("rw", this.#database.collections, async () => {
      const current = await this.#database.collections.get(entityId);
      if (current === undefined || current.deletedAt !== undefined) {
        throw new EntityNotFoundError("Collection", entityId);
      }
      const candidate = { ...current };
      delete candidate.archivedAt;
      const restored = CollectionSchema.parse({ ...candidate, updatedAt: this.#now() });
      await this.#database.collections.put(restored);
      return restored;
    });
  }

  async softDelete(id: string): Promise<Collection> {
    const entityId = parseEntityId(id);
    return this.#database.transaction("rw", this.#database.collections, async () => {
      const current = await this.#database.collections.get(entityId);
      if (current === undefined) {
        throw new EntityNotFoundError("Collection", entityId);
      }
      if (current.deletedAt !== undefined) {
        return CollectionSchema.parse(current);
      }
      const timestamp = this.#now();
      const deleted = CollectionSchema.parse({ ...current, deletedAt: timestamp, updatedAt: timestamp });
      await this.#database.collections.put(deleted);
      return deleted;
    });
  }

  async restore(id: string): Promise<Collection> {
    const entityId = parseEntityId(id);
    return this.#database.transaction("rw", this.#database.collections, async () => {
      const current = await this.#database.collections.get(entityId);
      if (current === undefined) {
        throw new EntityNotFoundError("Collection", entityId);
      }
      await this.#assertUniqueName(current.normalizedName, entityId);
      const candidate = { ...current };
      delete candidate.deletedAt;
      const restored = CollectionSchema.parse({ ...candidate, updatedAt: this.#now() });
      await this.#database.collections.put(restored);
      return restored;
    });
  }
}

export class TagRepository {
  readonly #database: DuaWorkspaceDatabase;
  readonly #now: () => string;
  readonly #createId: () => string;

  constructor(database: DuaWorkspaceDatabase, options: RepositoryOptions = {}) {
    this.#database = database;
    this.#now = options.now ?? (() => new Date().toISOString());
    this.#createId = options.createId ?? createUuid;
  }

  async list(includeDeleted = false): Promise<Tag[]> {
    let records = await this.#database.tags.toArray();
    if (!includeDeleted) {
      records = records.filter((record) => record.deletedAt === undefined);
    }
    return records.map((record) => TagSchema.parse(record)).sort(bySortOrderThenName((record) => record.name));
  }

  async getById(id: string, includeDeleted = false): Promise<Tag | undefined> {
    const record = await this.#database.tags.get(parseEntityId(id));
    if (record === undefined || (!includeDeleted && record.deletedAt !== undefined)) {
      return undefined;
    }
    return TagSchema.parse(record);
  }

  async #assertUniqueName(normalizedName: string, exceptId?: string): Promise<void> {
    const candidates = await this.#database.tags.where("normalizedName").equals(normalizedName).toArray();
    if (candidates.some((record) => record.deletedAt === undefined && record.id !== exceptId)) {
      throw new DuplicateEntityError("Tag", normalizedName);
    }
  }

  async create(input: CreateTagInput): Promise<Tag> {
    const values = TagCreateSchema.parse(input);
    const normalizedName = normalizeLabel(values.name);
    const timestamp = this.#now();
    const record = TagSchema.parse({
      id: this.#createId(),
      ...values,
      normalizedName,
      createdAt: timestamp,
      updatedAt: timestamp,
    });
    return this.#database.transaction("rw", this.#database.tags, async () => {
      await this.#assertUniqueName(normalizedName);
      await this.#database.tags.add(record);
      return record;
    });
  }

  async update(id: string, input: UpdateTagInput): Promise<Tag> {
    const entityId = parseEntityId(id);
    const patch = TagUpdateSchema.parse(input);
    return this.#database.transaction("rw", this.#database.tags, async () => {
      const current = await this.#database.tags.get(entityId);
      if (current === undefined || current.deletedAt !== undefined) {
        throw new EntityNotFoundError("Tag", entityId);
      }
      const normalizedName = patch.name === undefined ? current.normalizedName : normalizeLabel(patch.name);
      await this.#assertUniqueName(normalizedName, entityId);
      const updated = TagSchema.parse({ ...current, ...patch, normalizedName, updatedAt: this.#now() });
      await this.#database.tags.put(updated);
      return updated;
    });
  }

  async softDelete(id: string): Promise<Tag> {
    const entityId = parseEntityId(id);
    return this.#database.transaction("rw", this.#database.tags, async () => {
      const current = await this.#database.tags.get(entityId);
      if (current === undefined) {
        throw new EntityNotFoundError("Tag", entityId);
      }
      if (current.deletedAt !== undefined) {
        return TagSchema.parse(current);
      }
      const timestamp = this.#now();
      const deleted = TagSchema.parse({ ...current, deletedAt: timestamp, updatedAt: timestamp });
      await this.#database.tags.put(deleted);
      return deleted;
    });
  }

  async restore(id: string): Promise<Tag> {
    const entityId = parseEntityId(id);
    return this.#database.transaction("rw", this.#database.tags, async () => {
      const current = await this.#database.tags.get(entityId);
      if (current === undefined) {
        throw new EntityNotFoundError("Tag", entityId);
      }
      await this.#assertUniqueName(current.normalizedName, entityId);
      const candidate = { ...current };
      delete candidate.deletedAt;
      const restored = TagSchema.parse({ ...candidate, updatedAt: this.#now() });
      await this.#database.tags.put(restored);
      return restored;
    });
  }

  async merge(sourceId: string, targetId: string): Promise<Tag> {
    const sourceEntityId = parseEntityId(sourceId);
    const targetEntityId = parseEntityId(targetId);
    if (sourceEntityId === targetEntityId) {
      const existing = await this.getById(targetEntityId);
      if (existing === undefined) {
        throw new EntityNotFoundError("Tag", targetEntityId);
      }
      return existing;
    }

    return this.#database.transaction(
      "rw",
      this.#database.tags,
      this.#database.personalDuas,
      this.#database.sessionBlocks,
      async () => {
        const [source, target] = await Promise.all([
          this.#database.tags.get(sourceEntityId),
          this.#database.tags.get(targetEntityId),
        ]);
        if (source === undefined || source.deletedAt !== undefined) {
          throw new EntityNotFoundError("Tag", sourceEntityId);
        }
        if (target === undefined || target.deletedAt !== undefined) {
          throw new EntityNotFoundError("Tag", targetEntityId);
        }
        const timestamp = this.#now();
        await this.#database.personalDuas
          .where("tagIds")
          .equals(sourceEntityId)
          .modify((dua) => {
            dua.tagIds = [...new Set(dua.tagIds.map((id) => (id === sourceEntityId ? targetEntityId : id)))];
            dua.updatedAt = timestamp;
          });
        await this.#database.sessionBlocks.toCollection().modify((block) => {
          if (block.type === "tag" && block.sourceId === sourceEntityId) {
            block.sourceId = targetEntityId;
            block.updatedAt = timestamp;
          }
        });
        await this.#database.tags.put(
          TagSchema.parse({ ...source, deletedAt: timestamp, updatedAt: timestamp }),
        );
        return TagSchema.parse(target);
      },
    );
  }
}

export interface PersonCounts {
  active: number;
  archived: number;
}

export class PersonRepository {
  readonly #database: DuaWorkspaceDatabase;
  readonly #now: () => string;
  readonly #createId: () => string;

  constructor(database: DuaWorkspaceDatabase, options: RepositoryOptions = {}) {
    this.#database = database;
    this.#now = options.now ?? (() => new Date().toISOString());
    this.#createId = options.createId ?? createUuid;
  }

  async list(options: OrganisationalListOptions = {}): Promise<Person[]> {
    let records = await this.#database.people.toArray();
    if (options.includeDeleted !== true) {
      records = records.filter((record) => record.deletedAt === undefined);
    }
    if (options.includeArchived !== true) {
      records = records.filter((record) => record.archivedAt === undefined);
    }
    return records.map((record) => PersonSchema.parse(record)).sort(bySortOrderThenName((record) => record.displayName));
  }

  async getById(id: string, includeDeleted = false): Promise<Person | undefined> {
    const record = await this.#database.people.get(parseEntityId(id));
    if (record === undefined || (!includeDeleted && record.deletedAt !== undefined)) {
      return undefined;
    }
    return PersonSchema.parse(record);
  }

  async create(input: CreatePersonInput): Promise<Person> {
    const values = PersonCreateSchema.parse(input);
    const timestamp = this.#now();
    const record = PersonSchema.parse({
      id: this.#createId(),
      ...values,
      normalizedName: normalizeLabel(values.displayName),
      createdAt: timestamp,
      updatedAt: timestamp,
    });
    await this.#database.people.add(record);
    return record;
  }

  async update(id: string, input: UpdatePersonInput): Promise<Person> {
    const entityId = parseEntityId(id);
    const patch = PersonUpdateSchema.parse(input);
    return this.#database.transaction("rw", this.#database.people, async () => {
      const current = await this.#database.people.get(entityId);
      if (current === undefined || current.deletedAt !== undefined) {
        throw new EntityNotFoundError("Person", entityId);
      }
      const updated = PersonSchema.parse({
        ...current,
        ...patch,
        normalizedName:
          patch.displayName === undefined ? current.normalizedName : normalizeLabel(patch.displayName),
        updatedAt: this.#now(),
      });
      await this.#database.people.put(updated);
      return updated;
    });
  }

  async archive(id: string): Promise<Person> {
    return this.update(id, { archivedAt: this.#now() });
  }

  async markRemembered(id: string, rememberedAt: string = this.#now()): Promise<Person> {
    return this.update(id, { lastRememberedAt: rememberedAt });
  }

  async counts(id: string): Promise<PersonCounts> {
    const personId = parseEntityId(id);
    const records = await this.#database.personalDuas.where("personIds").equals(personId).toArray();
    return {
      active: records.filter(
        (dua) => dua.deletedAt === undefined && !["archived", "resolved"].includes(dua.status),
      ).length,
      archived: records.filter(
        (dua) => dua.deletedAt === undefined && ["archived", "resolved"].includes(dua.status),
      ).length,
    };
  }

  async softDelete(id: string): Promise<Person> {
    const entityId = parseEntityId(id);
    return this.#database.transaction("rw", this.#database.people, async () => {
      const current = await this.#database.people.get(entityId);
      if (current === undefined) {
        throw new EntityNotFoundError("Person", entityId);
      }
      if (current.deletedAt !== undefined) {
        return PersonSchema.parse(current);
      }
      const timestamp = this.#now();
      const deleted = PersonSchema.parse({ ...current, deletedAt: timestamp, updatedAt: timestamp });
      await this.#database.people.put(deleted);
      return deleted;
    });
  }

  async restore(id: string): Promise<Person> {
    const entityId = parseEntityId(id);
    return this.#database.transaction("rw", this.#database.people, async () => {
      const current = await this.#database.people.get(entityId);
      if (current === undefined) {
        throw new EntityNotFoundError("Person", entityId);
      }
      const candidate = { ...current };
      delete candidate.deletedAt;
      const restored = PersonSchema.parse({ ...candidate, updatedAt: this.#now() });
      await this.#database.people.put(restored);
      return restored;
    });
  }
}
